import { Button } from "@ews-admin/global-design-system";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { clearError, selectError } from "../../../store/reducers/errorSlice";

export const ErrorModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { error, isModalOpen, isGenericError } = useSelector(selectError);

  // Close on ESC key press
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isModalOpen) {
        dispatch(clearError());
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isModalOpen, dispatch]);

  if (!isModalOpen || !error) {
    return null;
  }

  // Field names come back as technical paths ("user.locations[0].coordinates"):
  // show a translated label for the last segment, or nothing when there is none.
  const fieldLabel = (name: string): string => {
    const leaf =
      name
        .split(".")
        .pop()
        ?.replace(/\[\d+\]/g, "") ?? "";
    return leaf ? t(`error.fieldLabels.${leaf}`, { defaultValue: "" }) : "";
  };

  // For API errors, get the title and description from translations
  const title = isGenericError
    ? t("error.generic.title")
    : t(`error.${error.code}.title`);

  const description = isGenericError
    ? t("error.generic.description")
    : t(`error.${error.code}.description`);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
        <div className="mb-4 text-center">
          <h2 className="text-xl font-bold text-red-600">{title}</h2>
          <p className="mt-2 text-gray-700">{description}</p>

          {/* Display field errors if any */}
          {error.fields && error.fields.length > 0 && (
            <div className="mt-4">
              <h3 className="text-md font-semibold">
                {t("error.fieldErrors")}
              </h3>
              <ul className="mt-2 list-disc pl-5 text-left text-sm text-red-500">
                {error.fields.map((field, index) => {
                  const label = fieldLabel(field.name);
                  return (
                    <li key={index}>
                      {label && <span className="font-semibold">{label}:</span>}{" "}
                      {field.message}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        <div className="flex justify-center">
          <Button variant="ghost" onClick={() => dispatch(clearError())}>
            {t("common.close")}
          </Button>

          {isGenericError && (
            <Link
              to="/#contact-section"
              className="ml-2 rounded bg-gray-600 px-4 py-2 text-white hover:bg-gray-700 focus:outline-none"
            >
              {t("error.contactSupport")}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default ErrorModal;
